/**
 * The provenance chain for a vault-grounded answer: where the value came from,
 * resolved warehouse → vault function → datasource → value.
 */
export interface AnswerProvenance {
  vault_function?: string | null;
  datasource?: string | null;
  value?: string | null;
  as_of?: string | null;
}

/**
 * The fields `buildProvenanceTrace` reads from a question. `DdqSessionQuestion`
 * satisfies this structurally; `provenance` is optional so the function works
 * whether or not the backend has populated the chain.
 */
export interface ProvenanceInput {
  answer_source?: 'vault_data' | 'knowledge_base' | 'llm' | 'firm_required' | null;
  citations?: { source_name: string | null; as_of_date: string | null }[];
  provenance?: AnswerProvenance | null;
}

export type ProvenanceKind = 'vault' | 'knowledge_base' | 'llm' | 'none';

/**
 * A renderable provenance summary.
 *
 * - `vault` answers carry a `steps` chain (rendered as warehouse → … → value).
 * - `knowledge_base` and `llm` answers carry a `note` instead of a chain.
 * - `none` (firm-required or unknown source) renders nothing.
 */
export interface ProvenanceResult {
  kind: ProvenanceKind;
  steps: string[];
  note: string | null;
}

/**
 * Builds the provenance trace for an answer. This is the product's
 * differentiator made visible: a vault-grounded answer shows its full chain
 * back to the warehouse, while other sources degrade to an honest one-line note.
 */
export function buildProvenanceTrace(input: ProvenanceInput): ProvenanceResult {
  const source = input.answer_source;

  if (source === 'vault_data') {
    const steps = ['Warehouse'];
    const p = input.provenance;
    if (p && (p.vault_function || p.datasource || p.value)) {
      if (p.vault_function) steps.push(p.vault_function);
      if (p.datasource) steps.push(p.datasource);
      if (p.value) steps.push(p.as_of ? `${p.value} · ${p.as_of}` : p.value);
    } else {
      steps.push('Live vault data');
    }
    return { kind: 'vault', steps, note: null };
  }

  if (source === 'knowledge_base') {
    const names = (input.citations ?? [])
      .map((c) => c.source_name)
      .filter((n): n is string => !!n);
    const note = names.length
      ? `From knowledge base: ${names.join(', ')}`
      : 'From knowledge base';
    return { kind: 'knowledge_base', steps: [], note };
  }

  if (source === 'llm') {
    return { kind: 'llm', steps: [], note: 'AI-generated — verify before sending' };
  }

  return { kind: 'none', steps: [], note: null };
}
